import { useCallback, useEffect, useState } from 'react'
import {
    collection,
    doc,
    onSnapshot,
    query,
    orderBy,
    addDoc,
    serverTimestamp,
    updateDoc,
    deleteDoc
} from 'firebase/firestore'
import type { User } from 'firebase/auth'
import type { Message, Conversation } from '../types'
import { db } from '../../../firebase'

interface UseChatFirestoreOptions {
    user: User | null
    lang: string
}

export function useChatFirestore({ user, lang }: UseChatFirestoreOptions) {
    const [conversations, setConversations] = useState<Conversation[]>([])
    const [activeId, setActiveId] = useState<string | null>(null)
    const [messages, setMessages] = useState<Message[]>([])

    // Listen for conversations list
    useEffect(() => {
        if (!user) {
            setConversations([])
            setActiveId(null)
            return
        }

        const convRef = collection(db, 'users', user.uid, 'conversations')
        const q = query(convRef, orderBy('updatedAt', 'desc'))

        return onSnapshot(q, (snap) => {
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() } as Conversation))
            setConversations(list)
        })
    }, [user])

    // Listen for messages in active conversation
    useEffect(() => {
        if (!user || !activeId) {
            setMessages([])
            return
        }

        const msgRef = collection(db, 'users', user.uid, 'conversations', activeId, 'messages')
        const q = query(msgRef, orderBy('createdAt', 'asc'))

        return onSnapshot(q, (snap) => {
            const msgs = snap.docs.map(d => d.data() as Message)
            setMessages(msgs)
        })
    }, [user, activeId])

    const startNewChat = useCallback(() => {
        setActiveId(null)
        setMessages([])
    }, [])

    const selectConversation = useCallback((id: string) => {
        setActiveId(id)
    }, [])

    const addMessage = async (message: Message, conversationId?: string) => {
        if (!user) return null

        let currentId = conversationId || activeId

        // If no active session, create one on the first message
        if (!currentId) {
            const convRef = collection(db, 'users', user.uid, 'conversations')
            const newConv = await addDoc(convRef, {
                title: message.content.slice(0, 40) + (message.content.length > 40 ? '...' : ''),
                lang,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                lastMessage: message.content
            })
            currentId = newConv.id
            setActiveId(currentId)
        } else {
            // Update last message and timestamp
            const convDoc = doc(db, 'users', user.uid, 'conversations', currentId)
            const updateData: any = {
                updatedAt: serverTimestamp()
            }
            if (message.role === 'user') {
                updateData.lastMessage = message.content
            }
            await updateDoc(convDoc, updateData)
        }

        // Add message to sub-collection
        const msgRef = collection(db, 'users', user.uid, 'conversations', currentId, 'messages')
        await addDoc(msgRef, {
            ...message,
            createdAt: serverTimestamp()
        })

        return currentId
    }

    const deleteConversation = async (id: string) => {
        if (!user) return
        const convDoc = doc(db, 'users', user.uid, 'conversations', id)
        await deleteDoc(convDoc)
        if (activeId === id) {
            setActiveId(null)
            setMessages([])
        }
    }

    const renameConversation = async (id: string, newTitle: string) => {
        if (!user) return
        const convDoc = doc(db, 'users', user.uid, 'conversations', id)
        await updateDoc(convDoc, { title: newTitle })
    }

    return {
        conversations,
        activeId,
        messages,
        startNewChat,
        selectConversation,
        addMessage,
        deleteConversation,
        renameConversation
    }
}
